/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: tabs\GeneralTab.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Extensions module.
----------------------------------------------------------------*/


import { Globe } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import type { DispatchLocationOption } from '../../../components/ui/SelectDispatchLocation';
import { useI18n } from '../../../hooks/useI18n';
import { ExtensionInfoBox } from '../components/ExtensionInfoBox';
import { ExtensionDeviceBox } from '../components/ExtensionDeviceBox';
import type { ExtensionFormState } from '../types';

interface GeneralTabProps {
    formData: ExtensionFormState;
    errors: Record<string, string>;
    isEditMode: boolean;
    classOfServices: Array<{ id: string | number; name: string }>;
    dialProfiles: Array<{ id: string | number; name: string }>;
    agentProfiles: Array<{ id: string | number; name: string }>;
    sipProfiles: Array<{ id: string | number; name: string; description?: string }>;
    smsNumbers: Array<{ id: string | number; phoneNumber: string; friendlyName?: string }>;
    dispatchLocations?: DispatchLocationOption[];
    isLoadingOptions: boolean;
    onChange: (field: string, value: unknown) => void;
}

export function GeneralTab({
    formData, errors, isEditMode, classOfServices, dialProfiles, agentProfiles,
    sipProfiles, smsNumbers, dispatchLocations = [], isLoadingOptions, onChange,
}: GeneralTabProps) {
    const { t } = useI18n();

    return (
        <div className="space-y-4">
            <ExtensionInfoBox
                formData={formData} errors={errors} isEditMode={isEditMode}
                classOfServices={classOfServices} dialProfiles={dialProfiles} agentProfiles={agentProfiles}
                smsNumbers={smsNumbers} isLoadingOptions={isLoadingOptions} onChange={onChange}
            />

            {formData.extension_type === 'sip' && (
                <ExtensionDeviceBox
                    formData={formData} errors={errors} sipProfiles={sipProfiles}
                    dispatchLocations={dispatchLocations} isLoadingOptions={isLoadingOptions} onChange={onChange}
                />
            )}

            {formData.extension_type === 'virtual' && (
                <FormBox title={t('extensions.virtual.title')} icon={<Globe className="h-5 w-5" />} variant="outlined">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-start">
                        <SimpleTooltip content={t('extensions.virtual.forwardTo.tooltip')} title={t('extensions.virtual.forwardTo.label')} position="top" maxWidth="300px">
                            <Label htmlFor="forwardTo" required className="text-left">{t('extensions.virtual.forwardTo.label')}</Label>
                        </SimpleTooltip>
                        <Input id="forwardTo" value={formData.forwardTo || ''} onChange={(e) => onChange('forwardTo', e.target.value)} placeholder={t('extensions.virtual.forwardTo.placeholder')} error={errors.forwardTo} />
                    </div>
                </FormBox>
            )}
        </div>
    );
}
